import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { todayString } from '../../utils/appDateTime';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import MobileInput from '../ui/mobile-input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import PlanPicker from '../subscription/PlanPicker';
import { priceForCycle } from '../subscription/PlanPricingTabs';
import AsyncSubmitButton, { ASYNC_SUBMIT_STATUS } from '../ui/AsyncSubmitButton';
import { Loader2 } from 'lucide-react';

function previewSchoolCode(schoolName) {
  const base = String(schoolName || '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
  return (base || 'SCH').slice(0, 12);
}

function employeeLabel(emp) {
  const name = [emp.first_name, emp.last_name].filter(Boolean).join(' ').trim();
  return emp.employee_code ? `${name} (${emp.employee_code})` : name || `Employee #${emp.id}`;
}

function apiErrorMessage(e, fallback = 'Could not create school account') {
  if (!e.response) {
    // eslint-disable-next-line no-console
    console.error('convert failed', e.code, e.message, e.response?.status, e.response?.data);
    if (e.code === 'ECONNABORTED') {
      return 'Request timed out. The school may still have been created — check the Schools list.';
    }
    return 'Server did not respond. The school may still have been created — check the Schools list. If empty, ensure production migrations are run.';
  }
  // eslint-disable-next-line no-console
  console.error('convert failed', e.code, e.message, e.response?.status, e.response?.data);
  return e.response?.data?.message || e.response?.data?.error || e.message || fallback;
}

export default function CrmConvertJoinDialog({ inquiryId, onClose, onSuccess }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_slug === 'super_admin';
  const [plans, setPlans] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [inquiryEmployeeId, setInquiryEmployeeId] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [loadingInquiry, setLoadingInquiry] = useState(true);
  const [form, setForm] = useState({
    school_name: '',
    school_code: '',
    school_code_manual: false,
    admin_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    joining_date: todayString(),
    package: '',
    amount: '',
    suggested_plan_id: '',
    payment_status: 'pending',
    remarks: '',
    credit_employee_id: '',
  });
  const [submitStatus, setSubmitStatus] = useState(ASYNC_SUBMIT_STATUS.idle);
  const [resending, setResending] = useState(false);
  const [result, setResult] = useState(null);
  const pendingResultRef = useRef(null);

  const autoCode = useMemo(() => previewSchoolCode(form.school_name), [form.school_name]);

  useEffect(() => {
    api.get('subscription/plans')
      .then((res) => setPlans(res.data?.data?.items || []))
      .catch(() => setPlans([]));
    if (isSuperAdmin) {
      api.get('/crm/employees')
        .then((res) => setEmployees(res.data?.data?.items || []))
        .catch(() => setEmployees([]));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    setLoadingInquiry(true);
    api.get(`/crm/inquiries/${inquiryId}`)
      .then((res) => {
        const inquiry = res.data?.data;
        if (! inquiry) return;
        const name = inquiry.school_name || '';
        const assignedId = inquiry.employee_id ? String(inquiry.employee_id) : '';
        setInquiryEmployeeId(assignedId || null);
        setForm((f) => ({
          ...f,
          school_name: name,
          school_code: previewSchoolCode(name),
          school_code_manual: false,
          admin_name: inquiry.principal_name || '',
          email: inquiry.email || '',
          phone: inquiry.contact_number || '',
          address: inquiry.full_address || '',
          city: inquiry.city || '',
          state: inquiry.state || '',
          credit_employee_id: assignedId,
        }));
      })
      .catch(() => {
        toast({ title: 'Error', description: 'Could not load inquiry', variant: 'destructive' });
      })
      .finally(() => setLoadingInquiry(false));
  }, [inquiryId, toast]);

  useEffect(() => {
    if (!form.school_code_manual) {
      setForm((f) => ({ ...f, school_code: autoCode }));
    }
  }, [autoCode, form.school_code_manual]);

  const handlePlanAmountChange = (plan, amount) => {
    setForm((f) => ({
      ...f,
      amount: amount > 0 ? String(amount) : f.amount,
    }));
  };

  const handleSelectPlan = (planId) => {
    setForm((f) => ({ ...f, suggested_plan_id: planId ? String(planId) : '' }));
    const plan = plans.find((p) => String(p.id) === String(planId));
    if (plan) {
      const amount = priceForCycle(plan, cycle);
      setForm((f) => ({
        ...f,
        suggested_plan_id: String(planId),
        amount: amount > 0 ? String(amount) : f.amount,
      }));
    }
  };

  const validate = () => {
    if (!form.school_name.trim()) {
      toast({ title: 'School name required', variant: 'destructive' });
      return false;
    }
    if (!form.admin_name.trim()) {
      toast({ title: 'School admin name required', variant: 'destructive' });
      return false;
    }
    if (!form.email.trim()) {
      toast({ title: 'Email required', variant: 'destructive' });
      return false;
    }
    if (!form.suggested_plan_id) {
      toast({
        title: 'Plan required',
        description: 'Select a subscription plan before converting',
        variant: 'destructive',
      });
      return false;
    }
    if (isSuperAdmin && !inquiryEmployeeId && !form.credit_employee_id) {
      toast({
        title: 'Employee required',
        description: 'Select which CRM employee should be credited for this join',
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const submit = async ({ beginLoading }) => {
    if (!validate()) return;

    beginLoading();
    try {
      const selectedPlan = plans.find((p) => String(p.id) === String(form.suggested_plan_id));
      const res = await api.post(`/crm/inquiries/${inquiryId}/convert-to-joined`, {
        school_name: form.school_name.trim(),
        school_code: form.school_code.trim() || null,
        admin_name: form.admin_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        joining_date: form.joining_date,
        package: form.package,
        amount: parseFloat(form.amount) || 0,
        suggested_plan_id: parseInt(form.suggested_plan_id, 10),
        subscription_plan: selectedPlan?.name || null,
        payment_status: form.payment_status,
        remarks: form.remarks,
        billing_cycle: cycle,
        employee_id: form.credit_employee_id ? parseInt(form.credit_employee_id, 10) : undefined,
      }, { timeout: 90000 });

      const data = res.data?.data || {};
      pendingResultRef.current = {
        schoolId: data.school_id,
        schoolCode: data.school_code,
        schoolName: form.school_name.trim(),
        adminUserId: data.admin_user_id,
        credentialsSent: data.credentials_sent,
        emailError: data.email_error,
        tempPassword: data.temp_password,
        subscriptionActivated: data.subscription_activated,
      };
    } catch (e) {
      toast({ title: 'Error', description: apiErrorMessage(e), variant: 'destructive' });
      throw e;
    }
  };

  const handleSubmitSuccess = () => {
    if (pendingResultRef.current) {
      setResult(pendingResultRef.current);
      pendingResultRef.current = null;
    }
  };

  const handleResend = async () => {
    if (!result?.adminUserId) return;
    setResending(true);
    try {
      const res = await api.post('/auth/resend-credentials', { user_id: result.adminUserId });
      const sent = !res.data?.data?.temp_password;
      if (sent) {
        toast({ title: 'Credentials sent', description: 'Login email sent to the school admin.' });
        setResult((r) => ({ ...r, credentialsSent: true, emailError: null, tempPassword: null }));
      } else {
        toast({
          title: 'Email failed',
          description: res.data?.message || 'Password reset but email could not be sent.',
          variant: 'destructive',
        });
      }
    } catch (e) {
      toast({ title: 'Error', description: apiErrorMessage(e, 'Could not resend credentials'), variant: 'destructive' });
    } finally {
      setResending(false);
    }
  };

  const handleDone = () => {
    onSuccess();
    onClose();
  };

  if (result) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>School account created</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              <strong>{result.schoolName}</strong> has been provisioned and appears in Schools.
            </p>
            {result.schoolCode && (
              <p className="text-muted-foreground">
                School code: <span className="font-mono font-medium text-foreground">{result.schoolCode}</span>
              </p>
            )}
            {result.subscriptionActivated ? (
              <p className="text-green-700">Selected plan is active — school status is active.</p>
            ) : (
              <p className="text-amber-700">
                Plan saved for school admin — they can log in and pay for the selected plan.
              </p>
            )}
            {result.credentialsSent ? (
              <p className="text-green-700">Login credentials were emailed to the school admin.</p>
            ) : (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
                <p className="text-amber-800">
                  Credentials email could not be sent{result.emailError ? `: ${result.emailError}` : '.'}
                </p>
                {result.tempPassword && (
                  <p className="text-xs text-muted-foreground">
                    Temporary password: <span className="font-mono">{result.tempPassword}</span>
                  </p>
                )}
                <Button size="sm" variant="outline" onClick={handleResend} disabled={resending}>
                  {resending ? 'Sending…' : 'Resend credentials'}
                </Button>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
            <Button variant="outline" onClick={handleDone}>Close</Button>
            {result.schoolId && (
              <>
                <Button variant="outline" asChild>
                  <Link to={`/schools/${result.schoolId}`} onClick={() => onSuccess()}>School details</Link>
                </Button>
                <Button asChild className="bg-blue-600">
                  <Link to="/schools" onClick={() => onSuccess()}>View in Schools</Link>
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>Convert to Join</DialogTitle>
        </DialogHeader>

        {loadingInquiry ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-6">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">School account</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label>School name *</Label>
                  <Input
                    value={form.school_name}
                    onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>School code</Label>
                  <Input
                    value={form.school_code}
                    onChange={(e) => setForm({
                      ...form,
                      school_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                      school_code_manual: true,
                    })}
                    placeholder={autoCode}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Auto-generated if left blank at submit.</p>
                </div>
                <div>
                  <Label>School admin name *</Label>
                  <Input
                    value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <MobileInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
                <div>
                  <Label>State</Label>
                  <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Subscription *</h3>
              {isSuperAdmin && !inquiryEmployeeId && (
                <div>
                  <Label>Credit to employee *</Label>
                  <Select
                    value={form.credit_employee_id || ''}
                    onValueChange={(v) => setForm({ ...form, credit_employee_id: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={String(emp.id)}>
                          {employeeLabel(emp)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Required when the inquiry is not assigned to a sales employee.
                  </p>
                </div>
              )}
              <PlanPicker
                plans={plans}
                selectedPlanId={form.suggested_plan_id || null}
                onSelectPlan={handleSelectPlan}
                cycle={cycle}
                onCycleChange={setCycle}
                onPlanAmountChange={handlePlanAmountChange}
                excludeFree
              />
              <p className="text-xs text-muted-foreground">
                The selected plan is saved for the school. It activates immediately only when payment status is Paid; otherwise the school admin pays after login.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Payment status</Label>
                  <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">CRM details</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Joining date</Label>
                  <DateInput value={form.joining_date} onChange={(v) => setForm({ ...form, joining_date: v })} />
                </div>
                <div>
                  <Label>Package</Label>
                  <Input value={form.package} onChange={(e) => setForm({ ...form, package: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Remarks</Label>
                <Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitStatus !== ASYNC_SUBMIT_STATUS.idle}>Cancel</Button>
          <AsyncSubmitButton
            onAction={submit}
            onSuccess={handleSubmitSuccess}
            onStatusChange={setSubmitStatus}
            disabled={loadingInquiry}
            className="bg-green-600"
            loadingLabel="Creating school account…"
          >
            Convert to Join
          </AsyncSubmitButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
